import { useState, useRef, useEffect, memo } from "react";
import { UUID } from '../model/uuid';
import { Accordion, AccordionSummary, AccordionDetails, List } from '@mui/material'
import { MentionModel } from "../communication";
import { arrowSVG, crossSVG } from "../constants";


interface OntologyTypeProps{
    focusedType: number | undefined;
    activeStructureIds : [UUID, UUID, MentionModel][];
    index: number;
    selectTypeByIndex: (index?: number|'up'|'down') => void;
    typeName: string;
    mentionList: Set<string>;
    mapMentionToOntologyType: ((mentionId:string,type:string) => void);
    deleteMentionFromOntologyType: ((mentionId:string,type:string) => void);
    spanMap: Map<string, string>;
    disabled: boolean,
    currentMentionModel: MentionModel
}

function OntologyType(props: OntologyTypeProps){
    const [focused, setFocused] = useState<boolean>(props.index === props.focusedType)
    const [opened, setOpened] = useState<boolean>(false)    
    const typeEl = useRef<HTMLDivElement>(null)
    const mentionExists = props.activeStructureIds.length? props.mentionList.has(props.activeStructureIds[0][0].uuidString) : false;

    // setFocused when props change
    useEffect(() => {
        setFocused(props.focusedType === props.index)
    }, [props.focusedType])
    
    
    // Focus or blur this element when focused state changes
    useEffect(() => {
        if(focused) {
            typeEl.current?.focus()
        }else{
            typeEl.current?.blur()
        }
    },[props.activeStructureIds])

    useEffect(() => {
        if(focused) {
            typeEl.current?.focus()
        }else{
            typeEl.current?.blur()
            setOpened(false)
        }
    },[focused])


    // Adds and remove event listeners when they need to be updated
    useEffect(() => {
        const ref = typeEl.current
        ref?.addEventListener('keydown', onKeyDownHandler)
        return(() => ref?.removeEventListener('keydown', onKeyDownHandler))
    }, [props.selectTypeByIndex, mentionExists])
    


    const onKeyDownHandler = (e: KeyboardEvent) => {
        if(e.key === 'ArrowUp'){
            e.preventDefault()
            props.selectTypeByIndex('up')
        } else if(e.key === 'ArrowDown'){
            e.preventDefault()
            props.selectTypeByIndex('down')
        } else if(e.key === 'Enter' && props.activeStructureIds.length && !props.disabled){
            if(props.mapMentionToOntologyType !== undefined)
                props.mapMentionToOntologyType(props.activeStructureIds[0][0].uuidString, props.typeName)
        } else if(e.key === 'ArrowRight'){
            setOpened(true)
        } else if(e.key === 'ArrowLeft'){
            setOpened(false)
        }
    }

    const mentions: JSX.Element[] = []
    const mentionsToDelete: string[] = []
    props.mentionList.forEach(mentionUuid => {
        if(props.spanMap.has(mentionUuid)){
            const match = props.activeStructureIds.some(sId => sId[0].uuidString === mentionUuid)
            const className = `mention${match? ' active' : ''}`
            mentions.push(<div className={className} key={'mention' + mentionUuid}>
                {props.spanMap.get(mentionUuid)}
                <div className="delete-mention" onClick={() => {
                    props.deleteMentionFromOntologyType(mentionUuid,props.typeName);
                }
            }>{crossSVG}</div>
            </div>)
        } else {
            mentionsToDelete.push(mentionUuid)
        }
    })
    
    for(const mention of mentionsToDelete){
        props.deleteMentionFromOntologyType(mention,props.typeName);
    }

    let typeClassName = `type ${opened? "open" : ""} ${focused? "focused": ""} ${mentionExists? "highlighted" : ""} ${props.disabled? "disabled": ""}`
    const mentionListClassName = `mention-list ${opened? " open" : " closed"}`

    if(props.currentMentionModel === "entity"){
        typeClassName += " entity"
    } else {
        typeClassName += " situation"
    }

    return (
        <>
            <Accordion 
             square 
             disableGutters
             expanded={opened}
             sx={{
                width: '100%',
                boxShadow: 'none'
             }}
            >
                <AccordionSummary
                 sx={{
                    padding: 0,
                    "&.Mui-focusVisible": {
                        background: 'none'
                    },

                    "> .MuiAccordionSummary-content": {
                        margin: '0'
                    }
                 }}
                >
                    <div
                     className={typeClassName}
                     ref={typeEl}
                     onClick={(e) => {
                        e.stopPropagation();
                        if(focused && props.activeStructureIds.length && !props.disabled){
                            e.stopPropagation();
                            if(props.mapMentionToOntologyType !== undefined)
                                props.mapMentionToOntologyType(props.activeStructureIds[0][0].uuidString, props.typeName)
                        } else{
                            props.selectTypeByIndex(props.index);
                        }
                     }}
                     tabIndex={1}
                    >
                        <p className="name">{props.typeName}</p>
                        <p className="item-count">{mentions.length + " mentions"}</p>
                        <div className={`dropdown${mentions.length? "" : " invisible"}`} onClick={() => {
                            setOpened(open => !open)
                        }}>{arrowSVG}</div>
                    </div>
                </AccordionSummary>
                <AccordionDetails
                 sx={{
                    padding: '0',
                 }}
                 className={mentionListClassName}
                >
                    <List 
                     className={mentionListClassName}
                     sx={{
                        padding: '0'
                     }}
                    >
                        {mentions}
                    </List>
                </AccordionDetails>
            </Accordion>
        </>
    )
}

export default memo(OntologyType)